import { $, YO, alcance, aviso, dia, el, esc, limpiar, num, pedir, puede, sb } from './nucleo';
import { abrirComoFicha, abrirEnOtraPestana, cerrarComoFicha } from './navegacion';
import { abrirVenta } from './venta';
import { abrirClienteDesdeAfuera } from './clientes';

// =====================================================================
//  LA FICHA DE LA VUELTA  (D233, sesión 178)
// =====================================================================
//  Lo que Steven eligió el 4/11: abrir cualquier vuelta (también las
//  cerradas) y ver, en una ficha que ocupa la pestaña,
//    · cada parada en orden: el cliente, la dirección, qué pasó con cada
//      producto (entregado, no estaba, rechazado, reprogramado) y lo que se
//      cobró en esa puerta;
//    · los horarios: a qué hora salió, a qué hora volvió y cuánto duró;
//    · la rendición: lo cobrado, cuánto ya entró a la caja (cuándo, quién lo
//      recibió y en qué caja) y cuánto falta rendir;
//    · el pedido abre la ficha de la venta y el cliente la suya, con
//      «← Volver a la vuelta» (D233: una ficha encima de otra de otra pestaña).
//
//  SÓLO MUESTRA. Despachar, cerrar, sacar o cargar una parada sigue en las
//  tarjetas de la pestaña; la ficha no ofrece ningún botón que cambie algo.
//  Todo sale de vistas que ya existían: no hay migración.
//
//  Qué tapa cada lectura (D84, medido sobre la definición de cada vista):
//    · `v_vueltas` y `vuelta_resultados`: el RLS de `vueltas` (`vuelta.ver`,
//      o la vuelta es tuya). Si la vuelta se ve, se ven.
//    · `v_vuelta_paradas`, `v_items_pedido`, `pedidos` y `v_cobros`: el RLS de
//      `pedidos` (y `clientes` para las paradas). Sin `pedido.ver` sólo se ven
//      los de TU vuelta mientras está viva; cerrada, ya no.
//    · `v_rendiciones`: JOIN interno a `cajas` (`caja.ver`).
//  Un vacío que pudo ser un permiso no dice «no hay» (regla 5).

let FV: any = null;      //  la ficha abierta: { id, v, ... }
let TURNO = 0;           //  una lectura vieja no pisa la ficha de otra vuelta

const CHIP: Record<string, string> = { armada:'c-warn', en_calle:'c-pen', cerrada:'c-ok' };
const TEXTO: Record<string, string> = { armada:'armada, todavía en el depósito',
                                        en_calle:'en la calle', cerrada:'cerrada' };
const RESULTADO: Record<string, [string, string]> = {
  entregado:    ['Entregado',    'c-ok'],
  no_estaba:    ['No estaba',    'c-warn'],
  rechazado:    ['Rechazado',    'c-bad'],
  reprogramado: ['Reprogramado', 'c-warn'],
};

const hora = (t: string | null | undefined) => t
  ? new Date(t).toLocaleTimeString('es-AR', { hour:'2-digit', minute:'2-digit', hour12:false })
  : '';
//  El día (aaaa-mm-dd) de un instante, en la misma zona que `hora()`: la
//  del navegador, como el resto de la pantalla.
const diaDe = (t: string) => {
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
//  La hora con el día, si no es el de la vuelta (una vuelta que cruzó la medianoche).
const horaDe = (t: string | null | undefined, fechaVuelta: string) => {
  if (!t) return '—';
  return diaDe(t) === fechaVuelta ? hora(t) : `${dia(diaDe(t))} ${hora(t)}`;
};
const duracion = (desde: string, hasta: string) => {
  const min = Math.round((new Date(hasta).getTime() - new Date(desde).getTime()) / 60000);
  if (!(min >= 0)) return '—';
  const h = Math.floor(min / 60), m = min % 60;
  return h ? `${h} h${m ? ` ${m} min` : ''}` : `${m} min`;
};

//  Si la vuelta es MÍA y está viva: el RLS me deja ver sus pedidos sin
//  `pedido.ver` (`app.pedido_de_mi_vuelta`). Lo mismo que `esMia` de Vueltas.
const esMiaViva = (v: any) => Number(v?.motoquero_id) === Number(YO?.id)
  && (v?.estado === 'armada' || v?.estado === 'en_calle');
//  Lo que le falta a quien mira para ver los pedidos de esta vuelta, o nada.
const faltaPara = (v: any, ...claves: string[]) => esMiaViva(v) ? []
  : claves.filter(k => !puede(k.split('.')[0], k.split('.')[1]));
//  Y si los ve recortados (alcance `propio`).
const recortado = (v: any) => !esMiaViva(v) && puede('pedido','ver')
  && alcance('pedido','ver') !== 'todos';
const noPodes = (que: string, falta: string[]) =>
  `No podés ver ${que}: te falta <b>${falta.join('</b> y <b>')}</b>.`;

function limpiarFicha(){
  FV = null;
  TURNO++;
}

export function cerrarFichaVuelta(){
  limpiarFicha();
  cerrarComoFicha($('#fichaVuelta'));
}

export async function abrirVuelta(id: number){
  const turno = ++TURNO;
  limpiar('#fvMsg');
  $('#fvTitulo').textContent = 'Vuelta';
  $('#fvChips').innerHTML = '';
  $('#fvResumen').innerHTML = '';
  $('#fvParadas').innerHTML = '<p class="hint">Leyendo las paradas…</p>';
  $('#fvPlata').innerHTML = '<p class="hint">Leyendo la plata…</p>';
  FV = { id };
  abrirComoFicha($('#fichaVuelta'), { lista: 'las vueltas', nombre: 'la vuelta',
    boton: $('#fvVolver'), alCerrar: limpiarFicha });
  window.scrollTo({ top: 0 });

  const [vu, pa, re] = await Promise.all([
    pedir(sb.from('v_vueltas').select('*').eq('id', id)),
    pedir(sb.from('v_vuelta_paradas').select('*').eq('vuelta_id', id).order('orden').order('pedido_id')),
    pedir(sb.from('vuelta_resultados').select('id,pedido_item_id,resultado,motivo,cargado_por,ocurrido_en')
      .eq('vuelta_id', id).order('ocurrido_en').order('id')),
  ]);
  if (turno !== TURNO) return;
  if (vu.error){
    aviso('#fvMsg', 'bad', `No se pudo leer la vuelta (${vu.error.message}). ` +
      'Volvé a las vueltas y entrá de nuevo, o recargá con Ctrl+F5.');
    $('#fvParadas').innerHTML = ''; $('#fvPlata').innerHTML = '';
    return;
  }
  const v = (vu.data || [])[0];
  if (!v){
    aviso('#fvMsg', 'warn', 'Esta vuelta ya no está entre las que podés ver.');
    $('#fvParadas').innerHTML = ''; $('#fvPlata').innerHTML = '';
    return;
  }
  FV = { id, v, paradas: pa.data || [], ePar: pa.error?.message || '',
         res: re.data || [], eRes: re.error?.message || '' };
  pintarCabecera();

  //  Lo que cuelga de los pedidos de la vuelta: sus productos, a qué cliente
  //  es cada uno, los cobros y quién cargó cada resultado.
  const pids = FV.paradas.map((p: any) => p.pedido_id);
  const uids = [...new Set<number>(FV.res.map((r: any) => Number(r.cargado_por)))];
  const nada = () => Promise.resolve({ data: [] as any[], error: null });
  const [its, peds, cob, us, pen] = await Promise.all([
    pids.length ? pedir(sb.from('v_items_pedido').select('*').in('pedido_id', pids).order('id')) : nada(),
    pids.length ? pedir(sb.from('pedidos').select('id,cliente_id').in('id', pids)) : nada(),
    pedir(sb.from('v_cobros').select('*').eq('vuelta_id', id).order('ocurrido_en').order('id')),
    uids.length ? pedir(sb.from('usuarios').select('id,nombre').in('id', uids)) : nada(),
    pedir(sb.from('v_rendiciones_pendientes').select('*').eq('vuelta_id', id)),
  ]);
  if (turno !== TURNO) return;
  FV.items = its.data || []; FV.eItems = its.error?.message || '';
  FV.clienteDe = new Map((peds.data || []).map((p: any) => [Number(p.id), p.cliente_id]));
  FV.ePeds = peds.error?.message || '';
  FV.cobros = cob.data || []; FV.eCob = cob.error?.message || '';
  FV.quien = new Map((us.data || []).map((u: any) => [Number(u.id), u.nombre]));
  FV.eUs = us.error?.message || '';
  FV.pend = pen.data || []; FV.ePend = pen.error?.message || '';

  //  Las rendiciones en las que entraron sus cobros: quién las recibió y en qué caja.
  const rids = [...new Set<number>(FV.cobros.map((c: any) => c.rendicion_id).filter((x: any) => x != null).map(Number))];
  const faltaCaja = !puede('caja','ver') ? ['caja.ver'] : [];
  const rr = rids.length && !faltaCaja.length
    ? await pedir(sb.from('v_rendiciones').select('*').in('id', rids))
    : { data: [] as any[], error: null };
  if (turno !== TURNO) return;
  FV.rend = rr.data || []; FV.eRend = rr.error?.message || ''; FV.faltaCaja = faltaCaja;

  pintarParadas();
  pintarPlata();
}

function pintarCabecera(){
  const v = FV.v;
  $('#fvTitulo').textContent = `Vuelta ${v.numero} de ${v.motoquero}`;
  $('#fvChips').innerHTML = `<span class="chip ${CHIP[v.estado] || 'c-pen'}">${esc(TEXTO[v.estado] || v.estado)}</span>`;
  const salio = v.salida_en ? horaDe(v.salida_en, v.fecha) : null;
  const volvio = v.regreso_en ? horaDe(v.regreso_en, v.fecha) : null;
  $('#fvResumen').innerHTML = `
    <div><div class="hint">Motoquero</div><b>${esc(v.motoquero)}</b></div>
    <div><div class="hint">Fecha</div>${esc(dia(v.fecha))}</div>
    <div><div class="hint">Paradas</div>${num(Number(v.paradas))}</div>
    <div><div class="hint">Salió</div>${salio ? `<b>${esc(salio)}</b>` : '<span class="hint">todavía no salió</span>'}</div>
    <div><div class="hint">Volvió</div>${volvio ? `<b>${esc(volvio)}</b>`
      : v.estado === 'en_calle' ? '<span class="hint">todavía en la calle</span>' : '—'}</div>
    <div><div class="hint">Duró</div>${v.salida_en && v.regreso_en
      ? `<b id="fvDuracion">${esc(duracion(v.salida_en, v.regreso_en))}</b>`
      : v.salida_en ? `<span class="hint">va ${esc(duracion(v.salida_en, new Date().toISOString()))}</span>` : '—'}</div>
    ${v.notas ? `<div style="grid-column:1/-1"><div class="hint">Nota</div>${esc(v.notas)}</div>` : ''}`;
}

//  ---------- las paradas ----------
function pintarParadas(){
  const v = FV.v;
  const falta = faltaPara(v, 'pedido.ver', 'cliente.ver');
  if (FV.ePar){
    $('#fvParadas').innerHTML = `<div class="vacio">No se pudieron leer las paradas (${esc(FV.ePar)}). ` +
      'No quiere decir que no tenga: no se pudo preguntar.</div>';
    return;
  }
  if (!FV.paradas.length){
    //  `v.paradas` (de `v_vueltas`) no pasa por `pedidos`: si dice que hay y no
    //  llegó ninguna, es que no se pueden ver (como la tarjeta de la vuelta).
    $('#fvParadas').innerHTML = `<div class="vacio">${Number(v.paradas) > 0
      ? (falta.length ? noPodes('las paradas de esta vuelta', falta)
         : `Esta vuelta tiene ${v.paradas} ${Number(v.paradas) === 1 ? 'parada y no se pudo ver' : 'paradas y no se pudieron ver'}.`)
      : 'Esta vuelta no tiene ninguna parada.'}</div>`;
    return;
  }
  //  Los productos y los cobros pasan por `pedidos`: sin `pedido.ver` (y sin
  //  ser tu vuelta viva) un vacío no dice «no hay», dice qué falta.
  const faltaPed = faltaPara(v, 'pedido.ver');
  const veVenta = puede('pedido','ver');
  const veCliente = puede('cliente','ver');
  //  El último resultado de cada producto EN ESTA VUELTA (si se cargó dos
  //  veces, vale el último: `cargar_entrega` no lo permite, pero no se supone).
  const resDe = new Map<number, any>();
  for (const r of FV.res) resDe.set(Number(r.pedido_item_id), r);

  //  Avisos de lo que no se pudo leer o no llegó entero, arriba de todo.
  const avisos = [
    FV.eRes ? `No se pudo leer qué pasó con cada producto (${esc(FV.eRes)}): no quiere decir que no se haya cargado nada.` : '',
    FV.ePeds ? `No se pudo leer de qué cliente es cada pedido (${esc(FV.ePeds)}): el cliente no abre su ficha.` : '',
    //  `v_vueltas` cuenta las paradas sin pasar por `pedidos` ni `clientes`:
    //  si llegan menos, alguna no se puede ver (el recorte de `pedido.ver`
    //  lo dice abajo; esto cubre cualquier otra causa).
    !recortado(v) && FV.paradas.length < Number(v.paradas)
      ? `Esta vuelta tiene ${num(Number(v.paradas))} paradas y se ven ${num(FV.paradas.length)}: las demás no las podés ver.` : '',
  ].filter(Boolean).map(t => `<div class="aviso a-warn">${t}</div>`).join('');
  $('#fvParadas').innerHTML = avisos + FV.paradas.map((p: any) => {
    const its = FV.items.filter((i: any) => Number(i.pedido_id) === Number(p.pedido_id));
    const cob = FV.cobros.filter((c: any) => Number(c.pedido_id) === Number(p.pedido_id) && !c.es_anulacion);
    const horas = its.map((i: any) => resDe.get(Number(i.id))?.ocurrido_en).filter(Boolean).sort();
    const cuando = horas.length ? horaDe(horas[horas.length - 1], v.fecha) : '';
    const cli = FV.clienteDe.get(Number(p.pedido_id));
    const filas = its.map((i: any) => {
      const r = resDe.get(Number(i.id));
      const [txt, chip] = r ? (RESULTADO[r.resultado] || [r.resultado, 'c-pen']) : ['', ''];
      return `<tr data-item="${i.id}">
        <td><span class="mono">${esc(i.producto)}</span> ${esc(i.descripcion)}${
          Number(i.cantidad) > 1 ? ` <span class="hint">× ${num(Number(i.cantidad))}</span>` : ''}</td>
        <td class="mono">${esc(i.serie || '—')}</td>
        <td>${r ? `<span class="chip ${chip}">${esc(txt)}</span>`
          //  Sin poder leer los resultados no se afirma «nada cargado».
          : FV.eRes ? `<span class="hint">no se pudo leer · hoy: ${esc(i.estado_nombre || i.estado)}</span>`
          : v.estado === 'cerrada'
            ? `<span class="hint">nada cargado en esta vuelta · hoy: ${esc(i.estado_nombre || i.estado)}</span>`
            : `<span class="hint">${esc(i.estado_nombre || i.estado)}</span>`}</td>
        <td class="hint">${r ? esc(horaDe(r.ocurrido_en, v.fecha)) : ''}</td>
        <td>${esc(r?.motivo || '')}</td>
        <td class="hint">${r ? esc(FV.quien.get(Number(r.cargado_por))
          || (FV.eUs ? 'no se pudo leer' : '—')) : ''}</td>
      </tr>`;
    }).join('');
    const tabla = FV.eItems
      ? `<p class="hint">No se pudieron leer los productos (${esc(FV.eItems)}).</p>`
      : its.length ? `<div class="tw"><table class="t-tarjetas">
          <thead><tr><th>Producto</th><th>Serie</th><th>Qué pasó</th><th>Cuándo</th>
                     <th>Motivo</th><th>Cargó</th></tr></thead>
          <tbody>${filas}</tbody></table></div>`
      : `<p class="hint">${faltaPed.length ? noPodes('sus productos', faltaPed)
          : Number(p.items) > 0 ? 'No se pudieron ver sus productos.' : 'Sin productos.'}</p>`;
    return `<div class="confirmar fv-parada" data-parada="${p.pedido_id}">
      <div class="conf-tit">${num(Number(p.orden))} · ${veVenta
          ? `<a href="#" data-venta="${p.pedido_id}" class="mono">${esc(p.pedido)}</a>`
          : `<span class="mono">${esc(p.pedido)}</span>`} · ${veCliente && cli != null
          ? `<a href="#" data-cliente="${cli}">${esc(p.cliente)}</a>` : esc(p.cliente)}${
          cuando ? ` <span class="hint">· ${esc(cuando)}</span>` : ''}</div>
      <p class="conf-txt">${esc(p.direccion || 'SIN DIRECCIÓN')}${p.barrio ? ', ' + esc(p.barrio) : ''}${
        p.zona ? ' · ' + esc(p.zona) : ''}${p.horario_texto ? ' · ' + esc(p.horario_texto) : ''}
        · entregados ${num(Number(p.entregados))} de ${num(Number(p.items))}</p>
      ${tabla}
      <p class="conf-txt fv-cobrado">${faltaPed.length ? `<span class="hint">${noPodes('lo cobrado', faltaPed)}</span>`
        : FV.eCob ? `<span class="hint">No se pudieron leer los cobros (${esc(FV.eCob)}).</span>`
        : cob.length ? 'Cobrado acá: ' + cob.map((c: any) => `<span class="${c.anulado ? 'hint' : ''}">${
            esc(c.medio_pago)} ${esc(c.moneda)} <b>${num(Number(c.monto))}</b>${c.anulado ? ' (anulado)' : ''}</span>`).join(' · ')
        : '<span class="hint">No se cobró nada en esta parada.</span>'}</p>
    </div>`;
  }).join('') + (recortado(v)
    ? '<p class="hint">Sólo ves los pedidos que son tuyos: puede haber más paradas.</p>' : '');

  document.querySelectorAll<HTMLElement>('#fvParadas [data-venta]').forEach(a => a.onclick = e => {
    e.preventDefault();
    abrirEnOtraPestana('pedidos', () => abrirVenta(Number(a.dataset.venta)));
  });
  document.querySelectorAll<HTMLElement>('#fvParadas [data-cliente]').forEach(a => a.onclick = e => {
    e.preventDefault();
    abrirEnOtraPestana('clientes', () => abrirClienteDesdeAfuera(Number(a.dataset.cliente)));
  });
}

//  ---------- la plata ----------
function pintarPlata(){
  const v = FV.v;
  const falta = faltaPara(v, 'pedido.ver');
  if (falta.length){ $('#fvPlata').innerHTML = `<div class="vacio">${noPodes('la plata de esta vuelta', falta)}</div>`; return; }
  if (FV.eCob){
    $('#fvPlata').innerHTML = `<div class="vacio">No se pudieron leer los cobros (${esc(FV.eCob)}). ` +
      'No quiere decir que no tenga: no se pudo preguntar.</div>';
    return;
  }
  const cobros = FV.cobros as any[];
  if (!cobros.length){
    $('#fvPlata').innerHTML = `<div class="vacio">${v.estado === 'armada'
      ? 'Todavía no salió: no hay nada cobrado.' : 'En esta vuelta no se cobró nada.'}${
      recortado(v) ? ' <span class="hint">(de los pedidos que ves)</span>' : ''}</div>`;
    return;
  }
  //  Por moneda: lo cobrado neto (las anulaciones restan), lo ya rendido y lo
  //  que falta. Lo que falta se cuenta de los cobros, y `v_rendiciones_pendientes`
  //  lo confirma (si no coinciden, gana la vista de la base y se avisa).
  const monedas = [...new Set(cobros.map(c => c.moneda))];
  const suma = (f: (c: any) => boolean, m: string) =>
    cobros.filter(c => c.moneda === m && f(c)).reduce((s, c) => s + Number(c.monto || 0), 0);
  const filas = monedas.map(m => {
    const total = suma(() => true, m);
    const rendido = suma(c => c.rendicion_id != null, m);
    const faltaRendir = total - rendido;
    return `<tr data-moneda="${esc(m)}"><td>${esc(m)}</td>
      <td class="num"><b>${num(total)}</b></td>
      <td class="num">${num(rendido)}</td>
      <td class="num">${Math.abs(faltaRendir) > 0.005
        ? `<span class="chip c-warn">${num(faltaRendir)}</span>` : '<span class="chip c-ok">nada</span>'}</td></tr>`;
  }).join('');
  const difiere = !FV.ePend && !recortado(v) && monedas.some(m => {
    const p = FV.pend.find((x: any) => x.moneda === m);
    return Math.abs(Number(p?.a_rendir || 0) - (suma(() => true, m) - suma(c => c.rendicion_id != null, m))) > 0.005;
  });

  //  Las rendiciones en las que entró: una por fila, con lo de ESTA vuelta.
  //  En el orden en que se rindieron.
  const cuandoDe = (rid: any) => FV.rend.find((x: any) => Number(x.id) === Number(rid))?.ocurrido_en
    || cobros.find(c => c.rendicion_id === rid)?.rendido_en || '';
  const rids = [...new Set(cobros.map(c => c.rendicion_id).filter(x => x != null))]
    .sort((a, b) => String(cuandoDe(a)).localeCompare(String(cuandoDe(b))));
  const rend = rids.map(rid => {
    const de = cobros.filter(c => c.rendicion_id === rid);
    const r = FV.rend.filter((x: any) => Number(x.id) === Number(rid));
    const r0 = r[0];
    const enEsta = [...new Set(de.map(c => c.moneda))].map(m =>
      `${esc(m)} ${num(suma(c => c.rendicion_id === rid, m))}`).join(' · ');
    const cuando = r0?.ocurrido_en || de[0]?.rendido_en;
    //  `v_rendiciones.cobros` cuenta los que no son reversas: el original
    //  anulado también. Se compara igual.
    const deOtras = r0 && Number(r0.cobros) > de.filter(c => !c.es_anulacion).length;
    return `<li data-rendicion="${rid}">Rendido el <b>${esc(cuando ? `${dia(diaDe(cuando))} ${hora(cuando)}` : '—')}</b>: ${enEsta}${
      FV.faltaCaja.length ? ` <span class="hint">(quién lo recibió y en qué caja: te falta <b>caja.ver</b>)</span>`
      : FV.eRend ? ` <span class="hint">(no se pudo leer quién lo recibió: ${esc(FV.eRend)})</span>`
      : r0 ? ` · lo recibió <b>${esc(r0.recibido_por)}</b> en ${esc(r0.caja)}${
          r.some((x: any) => Math.abs(Number(x.diferencia || 0)) > 0.005)
            ? ` · <span class="chip c-bad">con diferencia: ${r.filter((x: any) => Math.abs(Number(x.diferencia || 0)) > 0.005)
                .map((x: any) => `${esc(x.moneda)} ${num(Number(x.diferencia))}`).join(' · ')}</span>` : ''}${
          deOtras ? ' <span class="hint">(esa rendición juntó también cobros de otras vueltas)</span>' : ''}`
      : ''}</li>`;
  }).join('');

  $('#fvPlata').innerHTML = `<div class="tw"><table class="t-tarjetas">
      <thead><tr><th>Moneda</th><th class="num">Cobrado</th><th class="num">Ya rendido</th>
                 <th class="num">Falta rendir</th></tr></thead>
      <tbody>${filas}</tbody></table></div>
    ${FV.ePend ? `<p class="hint">No se pudo comparar con lo que la caja tiene pendiente (${esc(FV.ePend)}).</p>` : ''}
    ${difiere ? '<div class="aviso a-warn">Lo que falta rendir según los cobros no coincide con lo que dice la caja. ' +
      'Mirá Caja → Rendiciones antes de recibir esta plata.</div>' : ''}
    ${recortado(v) ? '<p class="hint">Sólo ves los cobros de los pedidos que son tuyos.</p>' : ''}
    ${rids.length ? `<ul class="fv-rendiciones" style="margin:0.588rem 0 0;padding-left:1.2rem">${rend}</ul>`
      : '<p class="hint">Todavía no se rindió nada de esta vuelta.</p>'}`;
}

//  Un clic en algo de la ficha que no es un enlace no hace nada: la ficha
//  no cambia nada (los enlaces tienen su propio `onclick`).
$('#fichaVuelta').addEventListener('click', e => {
  if (el(e.target).closest('a[href="#"]')) e.preventDefault();
});
